// src/onboarding/AppSheets.tsx
import React, { createContext, useContext, useRef, useState, useEffect, useMemo } from 'react';
import AddPhotoSheet from './sheets/AddPhotoSheet';
import EnableLocationSheet from './sheets/EnableLocationSheet';
import PickInterestsSheet from './sheets/PickInterestsSheet';

type SheetName = 'addPhoto' | 'enableLocation' | 'pickInterests';

type AppSheetsAPI = {
  open: (name: SheetName) => Promise<boolean>;
  close: (result?: boolean) => void;
};

const AppSheetsContext = createContext<AppSheetsAPI | null>(null);

export const AppSheetsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // ✅ Fix: provide a valid initial value and correct generic (`SheetName | ''`)
  const [current, setCurrent] = useState<SheetName | ''>('');
  const resolverRef = useRef<(v: boolean) => void>();
  const openInFlightRef = useRef<boolean>(false);

  useEffect(() => {
    (window as any).appSheets = {
      open: (name: SheetName) => open(name),
      close: (result?: boolean) => close(result),
    } as AppSheetsAPI;

    return () => {
      if ((window as any).appSheets) delete (window as any).appSheets;
    };
  }, []);

  const close = (result: boolean = false) => {
    // Resolve any waiting promise
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = undefined;
    }
    openInFlightRef.current = false;
    setCurrent('');
  };

  const open = (name: SheetName) => {
    // If something is already open, cancel it first
    if (openInFlightRef.current && resolverRef.current) {
      try {
        resolverRef.current(false);
      } catch {}
      resolverRef.current = undefined;
    }

    openInFlightRef.current = true;

    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
      setCurrent(name);
    });
  };

  const value = useMemo<AppSheetsAPI>(() => ({ open, close }), []);

  return (
    <AppSheetsContext.Provider value={value}>
      {children}

      {/* Simple host for sheets */}
      {current !== '' && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40">
          <div className="w-full sm:max-w-md bg-theme-cards rounded-t-2xl sm:rounded-2xl shadow-card border border-theme-cards">
            {current === 'addPhoto' && <AddPhotoSheet onClose={close} />}
            {current === 'enableLocation' && <EnableLocationSheet onClose={close} />}
            {current === 'pickInterests' && <PickInterestsSheet onClose={close} />}
          </div>
        </div>
      )}
    </AppSheetsContext.Provider>
  );
};

export const useAppSheets = () => {
  const ctx = useContext(AppSheetsContext);
  if (!ctx) throw new Error('useAppSheets must be used within AppSheetsProvider');
  return ctx;
};
